import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SessionProfile } from '@/routes/manifest';
import { SESSION_UNAUTHORIZED_EVENT, clearSession, getSession, login, logout as performLogout } from '@/lib/session';

type SessionState = {
  session: SessionProfile | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  login: (returnUrl?: string, realm?: string) => void;
};

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);

  const loadSession = useCallback(async (options?: { silent?: boolean; force?: boolean }) => {
    const silent = options?.silent ?? false;
    const generation = ++requestGeneration.current;
    if (!silent) {
      setLoading(true);
      setSession(null);
      setError(null);
    }

    try {
      const profile = await getSession({ force: options?.force });
      if (generation !== requestGeneration.current) return;
      setSession(profile);
      setError(null);
    } catch (err) {
      if (generation !== requestGeneration.current) return;
      setSession(null);
      const message = err instanceof Error ? err.message : 'Failed to load session';
      console.error(message, err);
      setError(message);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSession({ force: true });
    return () => { requestGeneration.current += 1; clearSession(); };
  }, [loadSession]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void loadSession({ silent: true, force: true });
    }, 60 * 1000);
    return () => window.clearInterval(interval);
  }, [loadSession]);

  useEffect(() => {
    const handleFocus = () => {
      void loadSession({ silent: true, force: true });
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadSession]);

  useEffect(() => {
    const handleUnauthorized = () => {
      requestGeneration.current += 1;
      clearSession();
      setLoading(false);
      setSession(null);
      setError('Authentication required');
    };
    window.addEventListener(SESSION_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(SESSION_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  useEffect(() => {
    if (!session) return;
    const remaining = (session.authorizationExpiresAt ?? 0) - Date.now();
    const timer = window.setTimeout(() => {
      setSession(null);
      clearSession();
      void loadSession({ silent: true, force: true });
    }, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [session, loadSession]);

  const refresh = useCallback(async () => {
    clearSession();
    await loadSession({ force: true });
  }, [loadSession]);

  const handleLogout = useCallback(async () => {
    requestGeneration.current += 1;
    clearSession();
    setSession(null);
    setLoading(false);
    try {
      await performLogout();
    } finally {
      clearSession();
      setSession(null);
    }
  }, []);

  const value = useMemo<SessionState>(() => ({
    session,
    loading,
    error,
    refresh,
    logout: handleLogout,
    login
  }), [session, loading, error, refresh, handleLogout]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
